// @ts-check
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Button,
  Container,
  Layout,
  Row,
  TransitionReplace,
  Toast,
} from '@openedx/paragon';
import { Helmet } from 'react-helmet';
import {
  Add as IconAdd,
  CheckCircle as CheckCircleIcon,
} from '@openedx/paragon/icons';
import { useSelector } from 'react-redux';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useLocation } from 'react-router-dom';

import { LoadingSpinner } from '../generic/Loading';
import { getProcessingNotification } from '../generic/processing-notification/data/selectors';
import { RequestStatus } from '../data/constants';
import SubHeader from '../generic/sub-header/SubHeader';
import ProcessingNotification from '../generic/processing-notification';
import InternetConnectionAlert from '../generic/internet-connection-alert';
import DeleteModal from '../generic/delete-modal/DeleteModal';
import ConfigureModal from '../generic/configure-modal/ConfigureModal';
import AlertMessage from '../generic/alert-message';
import getPageHeadTitle from '../generic/utils';
import { getCurrentItem, getProctoredExamsFlag } from './data/selectors';
import { COURSE_BLOCK_NAMES } from './constants';
import HeaderNavigations from './header-navigations/HeaderNavigations';
import OutlineSideBar from './outline-sidebar/OutlineSidebar';
import StatusBar from './status-bar/StatusBar';
import EnableHighlightsModal from './enable-highlights-modal/EnableHighlightsModal';
import SectionCard from './section-card/SectionCard';
import SubsectionCard from './subsection-card/SubsectionCard';
import UnitCard from './unit-card/UnitCard';
import HighlightsModal from './highlights-modal/HighlightsModal';
import EmptyPlaceholder from './empty-placeholder/EmptyPlaceholder';
import PublishModal from './publish-modal/PublishModal';
import PageAlerts from './page-alerts/PageAlerts';
import DraggableList from '../generic/drag-helper/DraggableList';
import {
  canMoveSection,
  possibleUnitMoves,
  possibleSubsectionMoves,
} from '../generic/drag-helper/utils';
import { useCourseOutline } from './hooks';
import messages from './messages';
import { getTagsExportFile } from './data/api';
import { useCustomToast } from '../generic/custom-toast/useCustomToast';

const CourseOutline = ({ courseId }) => {
  const intl = useIntl();
  const location = useLocation();
  const { showToast } = useCustomToast()

  const {
    courseName,
    savingStatus,
    statusBarData,
    courseActions,
    sectionsList,
    isCustomRelativeDatesActive,
    isLoading,
    isReIndexShow,
    showSuccessAlert,
    isSectionsExpanded,
    isEnableHighlightsModalOpen,
    isInternetConnectionAlertFailed,
    isDisabledReindexButton,
    isHighlightsModalOpen,
    isPublishModalOpen,
    isConfigureModalOpen,
    isDeleteModalOpen,
    closeHighlightsModal,
    closePublishModal,
    handleConfigureModalClose,
    closeDeleteModal,
    openPublishModal,
    openConfigureModal,
    openDeleteModal,
    headerNavigationsActions,
    openEnableHighlightsModal,
    closeEnableHighlightsModal,
    handleEnableHighlightsSubmit,
    handleInternetConnectionFailed,
    handleOpenHighlightsModal,
    handleHighlightsFormSubmit,
    handleConfigureItemSubmit,
    handlePublishItemSubmit,
    handleEditSubmit,
    handleDeleteItemSubmit,
    handleDuplicateSectionSubmit,
    handleDuplicateSubsectionSubmit,
    handleDuplicateUnitSubmit,
    handleNewSectionSubmit,
    handleNewSubsectionSubmit,
    handleCreateNewCourseXBlock,
    handleNewUnitSubmit,
    getUnitUrl,
    handleVideoSharingOptionChange,
    handlePasteClipboardClick,
    notificationDismissUrl,
    discussionsSettings,
    discussionsIncontextFeedbackUrl,
    discussionsIncontextLearnmoreUrl,
    deprecatedBlocksInfo,
    proctoringErrors,
    mfeProctoredExamSettingsUrl,
    handleDismissNotification,
    advanceSettingsUrl,
    handleSectionDragAndDrop,
    handleSubsectionDragAndDrop,
    handleUnitDragAndDrop,
    errors
  } = useCourseOutline({ courseId });

  useEffect(() => {
    // Wait for the course data to load before exporting tags.
    if (courseId && courseName && location.hash === '#export-tags') {
      showToast(intl.formatMessage(messages.exportTagsCreatingToastMessage));
      getTagsExportFile(courseId, courseName).then(() => {
        showToast(intl.formatMessage(messages.exportTagsSuccessToastMessage));
      }).catch(() => {
        showToast(intl.formatMessage(messages.exportTagsErrorToastMessage));
      });

      // Delete `#export-tags` from location
      window.location.href = '#';
    }
  }, [location, courseId, courseName]);

  const [sections, setSections] = useState(sectionsList);

  const restoreSectionList = () => {
    setSections(() => [...sectionsList]);
  };

  const {
    isShow: isShowProcessingNotification,
    title: processingNotificationTitle,
  } = useSelector(getProcessingNotification);

  const currentItemData = useSelector(getCurrentItem);
  const deleteCategory = COURSE_BLOCK_NAMES[currentItemData.category]?.name
  const isLesson = currentItemData.category === COURSE_BLOCK_NAMES.sequential.id;

  const enableProctoredExams = useSelector(getProctoredExamsFlag);


  document.title = getPageHeadTitle(courseName, "Course Outline");

  /**
   * Move section to new index
   * @param {any} currentIndex
   * @param {any} newIndex
   */
  const updateSectionOrderByIndex = (currentIndex, newIndex) => {
    if (currentIndex === newIndex) {
      return;
    }
    setSections((prevSections) => {
      const newSections = arrayMove(prevSections, currentIndex, newIndex);
      handleSectionDragAndDrop(newSections.map(section => section.id));
      return newSections;
    });
  };

  /**
   * Uses details from move information and moves subsection
   * @param {any} section
   * @param {any} moveDetails
   * @returns {void}
   */
  const updateSubsectionOrderByIndex = (section, moveDetails) => {
    const { fn, args, sectionId } = moveDetails;
    if (!args) {
      return;
    }
    const [sectionsCopy, newSubsections] = fn(...args);
    if (newSubsections && sectionId) {
      setSections(sectionsCopy);
      handleSubsectionDragAndDrop(
        sectionId,
        section.id,
        newSubsections.map(subsection => subsection.id),
        restoreSectionList,
      );
    }
  };

  /**
   * Uses details from move information and moves unit
   * @param {any} section
   * @param {any} moveDetails
   * @returns {void}
   */
  const updateUnitOrderByIndex = (section, moveDetails) => {
    const {
      fn, args, sectionId, subsectionId,
    } = moveDetails;
    if (!args) {
      return;
    }
    const [sectionsCopy, newUnits] = fn(...args);
    if (newUnits && sectionId && subsectionId) {
      setSections(sectionsCopy);
      handleUnitDragAndDrop(
        sectionId,
        section.id,
        subsectionId,
        newUnits.map(unit => unit.id),
        restoreSectionList,
      );
    }
  };

  useEffect(() => {
    setSections(sectionsList);
  }, [sectionsList]);

  if (isLoading) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return (
      <Row className="m-0 mt-4 justify-content-center">
        <LoadingSpinner />
      </Row>
    );
  }
  console.log('items=sections======>>>>', sections);
  return (
    <>
      {/* <Helmet>
        <title>{getPageHeadTitle(courseName, intl.formatMessage(messages.headingTitle))}</title>
      </Helmet> */}
      <Container size="xl" className="px-4 container-screen w-fit overflow-hidden course-outline-wrapper">
        <section className="max-w-1200 course-outline-container overflow-hidden h-full d-flex flex-column">
          {/* <PageAlerts
            courseId={courseId}
            notificationDismissUrl={notificationDismissUrl}
            handleDismissNotification={handleDismissNotification}
            discussionsSettings={discussionsSettings}
            discussionsIncontextFeedbackUrl={discussionsIncontextFeedbackUrl}
            discussionsIncontextLearnmoreUrl={discussionsIncontextLearnmoreUrl}
            deprecatedBlocksInfo={deprecatedBlocksInfo}
            proctoringErrors={proctoringErrors}
            mfeProctoredExamSettingsUrl={mfeProctoredExamSettingsUrl}
            advanceSettingsUrl={advanceSettingsUrl}
            savingStatus={savingStatus}
            errors={errors}
          /> */}
          {/* <TransitionReplace>
            {showSuccessAlert ? (
              <AlertMessage
                key={intl.formatMessage(messages.alertSuccessAriaLabelledby)}
                show={showSuccessAlert}
                variant="success"
                icon={CheckCircleIcon}
                title={intl.formatMessage(messages.alertSuccessTitle)}
                description={intl.formatMessage(messages.alertSuccessDescription)}
                aria-hidden="true"
                aria-labelledby={intl.formatMessage(messages.alertSuccessAriaLabelledby)}
                aria-describedby={intl.formatMessage(messages.alertSuccessAriaDescribedby)}
              />
            ) : null}
          </TransitionReplace> */}
          <div className="card-header-sticky pb-3">
            <SubHeader
              title={intl.formatMessage(messages.headingTitle)}
              subtitle={intl.formatMessage(messages.headingSubtitle)}
              className={`${!errors?.outlineIndexApi && sections.length ? '' : 'outline-h-screen'}`}
              emptyPlaceholder={!errors?.outlineIndexApi && !sections.length && <EmptyPlaceholder
                onCreateNewSection={handleNewSectionSubmit}
              // childAddable={courseActions.childAddable}
              />}
              headerActions={(
                <HeaderNavigations
                  isReIndexShow={isReIndexShow && false}
                  isSectionsExpanded={isSectionsExpanded}
                  headerNavigationsActions={headerNavigationsActions}
                  isDisabledReindexButton={isDisabledReindexButton}
                  hasSections={Boolean(sectionsList.length)}
                  courseActions={courseActions}
                  errors={errors}
                />
              )}
            />
          </div>
          {/* <Layout
            lg={[{ span: 12 }]}
            md={[{ span: 12 }]}
            sm={[{ span: 12 }]}
            xs={[{ span: 12 }]}
            xl={[{ span: 12 }]}
          >
            <Layout.Element className="custom-scrollbar h-full overflow-auto"> */}
          <section className="course-outline-section custom-scrollbar h-full overflow-auto">
            {/* <StatusBar
                      courseId={courseId}
                      isLoading={isLoading}
                      statusBarData={statusBarData}
                      openEnableHighlightsModal={openEnableHighlightsModal}
                      handleVideoSharingOptionChange={handleVideoSharingOptionChange}
                    /> */}
            {!errors?.outlineIndexApi && (
              <div className="">
                {sections.length ? (
                  <>
                    <DraggableList
                      items={sections}
                      setSections={setSections}
                      restoreSectionList={restoreSectionList}
                      handleSectionDragAndDrop={handleSectionDragAndDrop}
                      handleSubsectionDragAndDrop={handleSubsectionDragAndDrop}
                      handleUnitDragAndDrop={handleUnitDragAndDrop}
                    >
                      <SortableContext
                        id="root"
                        items={sections}
                        strategy={verticalListSortingStrategy}
                      >
                        {sections.map((section, sectionIndex) => (
                          <SectionCard
                            key={section.id}
                            section={section}
                            index={sectionIndex}
                            canMoveItem={canMoveSection(sections)}
                            isSelfPaced={statusBarData.isSelfPaced}
                            isCustomRelativeDatesActive={isCustomRelativeDatesActive}
                            savingStatus={savingStatus}
                            onOpenHighlightsModal={handleOpenHighlightsModal}
                            onOpenPublishModal={openPublishModal}
                            onOpenConfigureModal={openConfigureModal}
                            onOpenDeleteModal={openDeleteModal}
                            onEditSectionSubmit={handleEditSubmit}
                            onDuplicateSubmit={handleDuplicateSectionSubmit}
                            isSectionsExpanded={isSectionsExpanded}
                            onNewSubsectionSubmit={handleNewSubsectionSubmit}
                            onOrderChange={updateSectionOrderByIndex}
                          >
                            <SortableContext
                              id={section.id}
                              items={section.childInfo.children}
                              strategy={verticalListSortingStrategy}
                            >
                              {section.childInfo.children.map((subsection, subsectionIndex) => (
                                <SubsectionCard
                                  key={subsection.id}
                                  section={section}
                                  subsection={subsection}
                                  index={subsectionIndex}
                                  isSectionsExpanded={isSectionsExpanded}
                                  getPossibleMoves={possibleSubsectionMoves(
                                    [...sections],
                                    sectionIndex,
                                    section,
                                    section.childInfo.children,
                                  )}
                                  isSelfPaced={statusBarData.isSelfPaced}
                                  isCustomRelativeDatesActive={isCustomRelativeDatesActive}
                                  savingStatus={savingStatus}
                                  onOpenPublishModal={openPublishModal}
                                  onOpenDeleteModal={openDeleteModal}
                                  onEditSubmit={handleEditSubmit}
                                  onDuplicateSubmit={handleDuplicateSubsectionSubmit}
                                  onOpenConfigureModal={openConfigureModal}
                                  onNewUnitSubmit={handleNewUnitSubmit}
                                  onOrderChange={updateSubsectionOrderByIndex}
                                  onPasteClick={handlePasteClipboardClick}
                                  courseId={courseId}
                                >
                                  <SortableContext
                                    id={subsection.id}
                                    items={subsection.childInfo.children}
                                    strategy={verticalListSortingStrategy}

                                  >
                                    {subsection.childInfo.children.map((unit, unitIndex) => (
                                      <UnitCard
                                        key={unit.id}
                                        unit={unit}
                                        subsection={subsection}
                                        section={section}
                                        isSelfPaced={statusBarData.isSelfPaced}
                                        isCustomRelativeDatesActive={isCustomRelativeDatesActive}
                                        index={unitIndex}
                                        handleCreateNewCourseXBlock={handleCreateNewCourseXBlock}
                                        getPossibleMoves={possibleUnitMoves(
                                          [...sections],
                                          sectionIndex,
                                          subsectionIndex,
                                          section,
                                          subsection,
                                          subsection.childInfo.children,
                                        )}
                                        savingStatus={savingStatus}
                                        onOpenPublishModal={openPublishModal}
                                        onOpenConfigureModal={openConfigureModal}
                                        onOpenDeleteModal={openDeleteModal}
                                        onEditSubmit={handleEditSubmit}
                                        onDuplicateSubmit={handleDuplicateUnitSubmit}
                                        getTitleLink={getUnitUrl}
                                        onOrderChange={updateUnitOrderByIndex}
                                        discussionsSettings={discussionsSettings}
                                      />
                                    ))}
                                  </SortableContext>
                                </SubsectionCard>
                              ))}
                            </SortableContext>
                          </SectionCard>
                        ))}
                      </SortableContext>
                    </DraggableList>
                    {/* {courseActions.childAddable && (
                              <Button
                                data-testid="new-section-button"
                                className="mt-4"
                                variant="outline-primary"
                                onClick={handleNewSectionSubmit}
                                iconBefore={IconAdd}
                                block
                              >
                                {intl.formatMessage(messages.newSectionButton)}
                              </Button>
                            )} */}
                  </>
                ) : (
                  null
                )}
              </div>
            )}
          </section>
          {/* </Layout.Element>
          </Layout> */}
          <EnableHighlightsModal
            isOpen={isEnableHighlightsModalOpen}
            close={closeEnableHighlightsModal}
            onEnableHighlightsSubmit={handleEnableHighlightsSubmit}
          />
        </section>
        <HighlightsModal
          isOpen={isHighlightsModalOpen}
          onClose={closeHighlightsModal}
          onSubmit={handleHighlightsFormSubmit}
        />
        <PublishModal
          isOpen={isPublishModalOpen}
          onClose={closePublishModal}
          onPublishSubmit={handlePublishItemSubmit}
        />
        <ConfigureModal
          isOpen={isConfigureModalOpen}
          onClose={handleConfigureModalClose}
          onConfigureSubmit={handleConfigureItemSubmit}
          currentItemData={currentItemData}
          enableProctoredExams={enableProctoredExams}
          isSelfPaced={statusBarData.isSelfPaced}
        />
        <DeleteModal
          category={deleteCategory}
          isOpen={isDeleteModalOpen}
          close={closeDeleteModal}
          onDeleteSubmit={handleDeleteItemSubmit}
          description={isLesson ? intl.formatMessage(messages.delLessonDescription) : intl.formatMessage(messages.delSectionDescription)}
          btnDefaultLabel={intl.formatMessage(messages.delBtnText, { category: deleteCategory })}
        />
      </Container>
      <div className="alert-toast">
        <ProcessingNotification
          isShow={isShowProcessingNotification}
          title={processingNotificationTitle}
        />
        <InternetConnectionAlert
          isFailed={isInternetConnectionAlertFailed}
          isQueryPending={savingStatus === RequestStatus.PENDING}
          onInternetConnectionFailed={handleInternetConnectionFailed}
        />
      </div>
    </>
  );
};

CourseOutline.propTypes = {
  courseId: PropTypes.string.isRequired,
};

export default CourseOutline;
